import {
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  useAgentContext,
  useAgentFocusedContent,
  usePageBrand,
  type AgentFocusFile,
} from "src/components/llm/agent-focus/agent-focus";

import {
  Box,
  CanvasIcon,
  Column,
  FileIcon,
  ImageIcon,
  MailIcon,
  Row,
  SearchIcon,
  TimeIcon,
} from "@hightouch/htui";
import { AgentType, CreationSource } from "@hightouch/lib/llm/shared";

import { ProjectPill } from "src/pages/agents/components/project-pill";
import { useFileTypeAccess } from "src/pages/projects/library/files/use-file-type-access";
import { ResourceAgentSidebar } from "src/components/layout/page-sidebar/agent/resource-agent-sidebar/resource-agent-sidebar";
import type { ResourceAgentConfig } from "src/components/layout/page-sidebar/agent/resource-agent-sidebar/types";
import { useAgentsSidebarAvailable } from "src/components/layout/page-sidebar/agent/resource-agent-sidebar/use-agents-sidebar-available";
import { AGENT_SIDEBAR_DOCK_BREAKPOINT } from "src/components/layout/page-sidebar/agent/constants";
import { SidebarName } from "src/components/layout/page-sidebar/constants";
import { isUnder } from "src/components/layout/nav/amp-nav-panels";
import {
  useIsFullscreenRoute,
  useIsFullscreenRouteDeclared,
} from "src/components/layout/fullscreen-route";
import {
  PageRailContext,
  ReportPageRailContext,
  usePageRail,
} from "src/components/page-rail";
import { PAPER_RIGHT_PADDING } from "src/components/paper";
import { AgentFocusProvider } from "src/components/llm/agent-focus/agent-focus-provider";
import {
  type ChatGutter,
  ChatGutterProvider,
} from "src/components/llm/context/chat-gutter-context";
import { AgentControlProvider } from "src/components/llm/embedded-chat/agent-control-context";
import { ORCHESTRATOR_AGENT_TYPE } from "@hightouch/lib/agent-harness/agents/orchestrator/agent-type";
import {
  HeaderHeightContext,
  useHeaderHeight,
} from "src/contexts/header-height-context";
import { useRefreshAmpFilesAfterTurns } from "src/pages/projects/amp-session";
import {
  AMP_CHAT_SCOPE,
  ampChatListCreationSources,
  PROJECTS_PATHS,
} from "src/pages/projects/constants";
import { AmpSubagents } from "src/pages/projects/amp-subagents";
import { SetProjectsAgentHiddenContext } from "src/pages/projects/projects-agent-hidden";
import {
  CanvasThreadTargetsContext,
  NO_RUNNING_THREADS,
  NO_THREAD_TARGETS,
  RunningThreadsContext,
  SetCanvasThreadTargetsContext,
  SetRunningThreadsContext,
  type ThreadTargets,
} from "src/pages/projects/library/canvas/canvas-thread-targets";
import {
  selectionComposerPlaceholder,
  useCanvasContextChips,
} from "src/pages/projects/library/canvas/use-canvas-context-chips";
import { useAmpComposer } from "src/pages/projects/use-amp-composer";
import { useProjectLinkWrites } from "src/pages/projects/use-linked-projects";
import { useProjectsAvailable } from "src/pages/projects/use-projects-available";
import { useNewAmpChatPrivacy } from "src/pages/projects/use-new-amp-chat-privacy";
import { useIsScreenAtLeast } from "src/hooks/use-is-large-screen";
import { useLocation } from "src/router";

const PROJECTS_INTRODUCTION = {
  title: "Ask about your projects",
  description:
    "Create a project, plan its tasks, or pick up work on the one that's open.",
};

const NO_SUGGESTIONS: ResourceAgentConfig["suggestions"] = [];
const NO_FILES: AgentFocusFile[] = [];

// Docked, the rail sits 8px from the page card and from the window. Per design, the composer then sits 16px from each and the conversation 32px.
const DOCKED_RAIL_CHAT_GUTTER: ChatGutter = { composer: 2, conversation: 6 };

const LIBRARY_INTRODUCTION = {
  title: "Ask about your library",
  description:
    "Find files, write documents, save uploads, and put files on canvases.",
};

const LIBRARY_SUGGESTIONS: ResourceAgentConfig["suggestions"] = [
  {
    icon: SearchIcon,
    title: "Find a file",
    description: "Search names across every folder",
    prompt: "Help me find a file in the library. Ask me what it was about.",
  },
  {
    icon: FileIcon,
    title: "Write a document",
    description: "In the folder that's open",
    prompt:
      "Ask me what the document is for, then write it in the folder I have open.",
  },
  {
    icon: CanvasIcon,
    title: "Start a canvas",
    description: "With files from the library",
    prompt:
      "Ask me which files belong together, then make a canvas and put them on it.",
  },
];

const CANVAS_SUGGESTIONS = [
  {
    icon: FileIcon,
    fileType: "doc",
    title: "Draft the brief",
    description: "Direction, audience, deliverables",
    prompt:
      "Ask me what the campaign is for, then draft a brief as a document on this canvas covering its creative direction and deliverables.",
  },
  {
    icon: TimeIcon,
    fileType: "doc",
    title: "Outline a launch plan",
    description: "A second doc, beside the brief",
    prompt:
      "Read the brief on this canvas, then outline a launch plan as a second document beside it.",
  },
  {
    icon: ImageIcon,
    fileType: "ad",
    title: "Make the first ads",
    description: "Two directions, then the brief",
    prompt:
      "Make the first ads for this campaign in two distinct creative directions.",
  },
  {
    icon: MailIcon,
    fileType: "email",
    title: "Make the first emails",
    description: "Two directions, then the brief",
    prompt:
      "Make the first emails for this campaign in two distinct creative directions, each in a template that fits.",
  },
] as const;

/**
 * The project-management area's pages, canvases included, beside the one AMP
 * agent chat. Mounted once for the area, so moving between its pages keeps
 * the chat, and a reply in progress, open.
 */
export function ProjectsAgentLayout({ children }: { children: ReactNode }) {
  const [threadTargets, setThreadTargets] = useState<ThreadTargets | null>(
    null,
  );
  const [runningThreads, setRunningThreads] =
    useState<ReadonlySet<string>>(NO_RUNNING_THREADS);
  const [sessionId, setSessionId] = useState<string | null>(null);
  useRefreshAmpFilesAfterTurns(sessionId);
  const [isAgentHidden, setAgentHidden] = useState(false);
  const isAgentAvailable = useAgentsSidebarAvailable();
  // The canvas, which renders flush whether or not a space keeps the nav beside it.
  const isFlushPage = useIsFullscreenRouteDeclared();
  const isFullWindow = useIsFullscreenRoute();
  const { isBesideRail, reportRail } = usePageRail(
    isAgentAvailable && !isAgentHidden,
  );

  return (
    <AgentFocusProvider>
      {/* Around the page as well as the chat, so a canvas tile can send to the chat. */}
      <AgentControlProvider agentAvailable={isAgentAvailable}>
        <SetCanvasThreadTargetsContext.Provider value={setThreadTargets}>
          {/* At least the viewport's height, so a contained page fills it and a long page still scrolls the window. */}
          <Row
            flex={1}
            minWidth={0}
            // Full window, the parents set no height, so 100% resolved only while the agent rail held the row up.
            minHeight={isFullWindow ? "100vh" : "100%"}
            align="stretch"
          >
            <Column
              flex={1}
              minWidth={0}
              // A flush page has no Paper, so this stands in for the Paper's right padding the rail mirrors at the window edge.
              pr={isFlushPage && isBesideRail ? PAPER_RIGHT_PADDING : undefined}
            >
              <PageRailContext.Provider value={isBesideRail}>
                {/* Only the area's own chat reports to the area, never a panel a page renders. */}
                <ReportPageRailContext.Provider value={null}>
                  <SetProjectsAgentHiddenContext.Provider
                    value={setAgentHidden}
                  >
                    <RunningThreadsContext.Provider value={runningThreads}>
                      {children}
                    </RunningThreadsContext.Provider>
                  </SetProjectsAgentHiddenContext.Provider>
                </ReportPageRailContext.Provider>
              </PageRailContext.Provider>
            </Column>
            {/* Hidden rather than unmounted, so a reply in progress survives a view without the chat. */}
            <Box display={isAgentHidden ? "none" : "contents"}>
              <CanvasThreadTargetsContext.Provider
                value={threadTargets ?? NO_THREAD_TARGETS}
              >
                <ReportPageRailContext.Provider value={reportRail}>
                  <SetRunningThreadsContext.Provider value={setRunningThreads}>
                    <ProjectsAgentSidebar onSessionChange={setSessionId} />
                  </SetRunningThreadsContext.Provider>
                </ReportPageRailContext.Provider>
              </CanvasThreadTargetsContext.Provider>
            </Box>
          </Row>
        </SetCanvasThreadTargetsContext.Provider>
      </AgentControlProvider>
    </AgentFocusProvider>
  );
}

function ProjectsAgentSidebar({
  onSessionChange,
}: {
  onSessionChange: (sessionId: string | null) => void;
}) {
  const isAvailable = useAgentsSidebarAvailable();
  // Below the dock breakpoint the rail overlays the page and keeps the chat's own gutter.
  const isDocked = useIsScreenAtLeast(AGENT_SIDEBAR_DOCK_BREAKPOINT);
  const headerHeight = useHeaderHeight();
  // Beside the whole page, banners included, so it spans the viewport rather than the space below them.
  const besideBanners = useMemo(
    () => ({ ...headerHeight, headerHeight: 0 }),
    [headerHeight],
  );
  const config = useAmpAgentConfig(onSessionChange);
  const { pathname } = useLocation();
  // The Library's New view has its own composer, so the chat stays out of the way until it has something to show.
  const isLibraryNewView = isUnder(pathname, PROJECTS_PATHS.libraryNew);

  if (!isAvailable) return null;
  return (
    <HeaderHeightContext.Provider value={besideBanners}>
      <ChatGutterProvider value={isDocked ? DOCKED_RAIL_CHAT_GUTTER : null}>
        <ResourceAgentSidebar
          config={config}
          collapseByDefault={isLibraryNewView}
        />
      </ChatGutterProvider>
    </HeaderHeightContext.Provider>
  );
}

/** Every page lists the same chats and sends what the person is looking at as focused content. */
function useAmpAgentConfig(
  onSessionChange: (sessionId: string | null) => void,
): ResourceAgentConfig {
  const { context } = useAgentContext();
  const canvas = context.canvas ?? null;
  // The same gate under which Home marks its chats as AMP chats.
  const isAmpV2 = useProjectsAvailable();
  const { onClearSelection } = useContext(CanvasThreadTargetsContext);
  const selectedFiles =
    canvas?.selectedFiles ?? context.library?.selectedFiles ?? NO_FILES;
  const chips = useCanvasContextChips(
    selectedFiles,
    onClearSelection,
    canvas ? "canvas" : "library",
  );
  const isLibrary = context.library !== undefined;
  const pageBrand = usePageBrand();
  const { composer, onSessionChange: onComposerSessionChange } = useAmpComposer(
    { pageBrand, contextChips: chips },
  );
  const focusedContent = useAgentFocusedContent();
  const access = useFileTypeAccess();
  const canvasSuggestions = useMemo(
    () =>
      CANVAS_SUGGESTIONS.filter((suggestion) => access[suggestion.fileType]),
    [access],
  );
  const canvasName = canvas?.name ?? null;
  // A chat started on a project's page belongs to that project, unless the person picks others.
  const focusedProjectId = context.project?.projectId ?? null;
  const { projectIds, setProjectIds, resetProjectIds } =
    useNewChatProjects(focusedProjectId);
  const { linkToProject } = useProjectLinkWrites();
  const privacy = useNewAmpChatPrivacy();
  const handleChatCreated = useCallback(
    (sessionId: string) => {
      resetProjectIds();
      for (const projectId of projectIds) {
        void linkToProject({ kind: "chat_session", id: sessionId }, projectId);
      }
    },
    [projectIds, linkToProject, resetProjectIds],
  );
  const handleSessionChange = useCallback(
    (sessionId: string | null) => {
      onComposerSessionChange(sessionId);
      onSessionChange(sessionId);
    },
    [onComposerSessionChange, onSessionChange],
  );

  return useMemo(
    () => ({
      resourceId: "amp",
      scope: AMP_CHAT_SCOPE,
      sidebarStorageKeyPrefix: "projects-agent-sidebar-was-closed",
      sidebarName: SidebarName.ProjectsAgent,
      agentType: AgentType.Routing,
      creationSource: CreationSource.Agents,
      listCreationSources: ampChatListCreationSources(isAmpV2),
      ampAgentType: ORCHESTRATOR_AGENT_TYPE,
      sessionContext: {},
      focusedContent,
      composerAccessory: AmpSubagents,
      onSessionChange: handleSessionChange,
      composer,
      newChat: {
        ...privacy,
        centerComposer: true,
        ...(isAmpV2 && {
          actions: (
            <ProjectPill
              selectedProjectIds={projectIds}
              onChange={setProjectIds}
            />
          ),
        }),
      },
      showProjectChips: true,
      onChatCreated: handleChatCreated,
      ...(isLibrary
        ? {
            introduction: LIBRARY_INTRODUCTION,
            suggestions: LIBRARY_SUGGESTIONS,
            composerPlaceholder: selectionComposerPlaceholder(
              "library",
              selectedFiles.length,
            ),
          }
        : canvasName === null
          ? {
              introduction: PROJECTS_INTRODUCTION,
              suggestions: NO_SUGGESTIONS,
              composerPlaceholder: "Ask about a project or task…",
            }
          : {
              introduction: {
                title: `Work on ${canvasName || "this canvas"}`,
                description: "Create and organize files on this canvas.",
              },
              suggestions: canvasSuggestions,
              composerPlaceholder: selectionComposerPlaceholder(
                "canvas",
                selectedFiles.length,
              ),
            }),
    }),
    [
      canvasSuggestions,
      focusedContent,
      isLibrary,
      canvasName,
      handleSessionChange,
      composer,
      privacy,
      projectIds,
      setProjectIds,
      selectedFiles.length,
      handleChatCreated,
      isAmpV2,
    ],
  );
}

/**
 * The projects the next new chat joins. Follows the open project until the person
 * picks, and goes back to it once a chat starts or another project opens.
 */
function useNewChatProjects(focusedProjectId: string | null) {
  const [picked, setPicked] = useState<{
    forProjectId: string | null;
    projectIds: string[];
  } | null>(null);
  const projectIds = useMemo(
    () =>
      picked && picked.forProjectId === focusedProjectId
        ? picked.projectIds
        : focusedProjectId
          ? [focusedProjectId]
          : [],
    [picked, focusedProjectId],
  );
  const setProjectIds = useCallback(
    (ids: string[]) =>
      setPicked({ forProjectId: focusedProjectId, projectIds: ids }),
    [focusedProjectId],
  );
  const resetProjectIds = useCallback(() => setPicked(null), []);
  return { projectIds, setProjectIds, resetProjectIds };
}
